import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import Content from "./Content";
import { UserContext } from "./UserContext";
import { io } from "socket.io-client";
import { useTheme } from "./ThemeContext";
import { Pen, Zap, Users, ChevronsRight } from 'lucide-react';


const SKELETON_COUNT = 6;

function PostSkeleton({ theme }) {
  const bgColor = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const shimmerColor = theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200';

  return (
    <div className={`${bgColor} rounded-xl shadow-lg flex flex-col animate-pulse overflow-hidden`}>
      <div className={`h-56 ${shimmerColor} w-full`} />
      <div className="flex-1 flex flex-col p-6">
        <div className={`h-6 ${shimmerColor} rounded w-2/3 mb-2`} />
        <div className="flex items-center space-x-2 mb-2">
          <div className={`h-4 w-16 ${shimmerColor} rounded`} />
          <div className={`h-4 w-8 ${shimmerColor} rounded`} />
        </div>
        <div className={`h-4 ${shimmerColor} rounded w-full mb-2`} />
        <div className={`h-4 ${shimmerColor} rounded w-5/6 mb-2`} />
        <div className={`h-4 ${shimmerColor} rounded w-1/2`} />
      </div>
    </div>
  );
}

const Post = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // Removed unused state: const [total, setTotal] = useState(0);
  const observer = useRef();
  const { userInfo } = useContext(UserContext);
  const { theme } = useTheme();

  const fetchPosts = useCallback(async (pageNum) => {
    setLoading(true);
    const res = await fetch(`${process.env.REACT_APP_API_URL}/post?page=${pageNum}&limit=9`);
    const data = await res.json();
    if (pageNum === 1) {
      setPosts(data.posts);
    } else {
      setPosts((prev) => [...prev, ...data.posts]);
    }
    // Removed unused state update: setTotal(data.total);
    setHasMore(data.posts.length > 0 && posts.length + data.posts.length < data.total);
    setLoading(false);
  }, [posts.length]);

  useEffect(() => {
    fetchPosts(page);
    // eslint-disable-next-line
  }, [page]);

  // Real-time updates with Socket.IO
  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_URL, {
      withCredentials: true,
      transports: ["websocket"],
    });
    socket.on("new_post", (newPost) => {
      setPosts((prev) => {
        if (prev.some((p) => p._id === newPost._id)) return prev;
        return [newPost, ...prev];
      });
      // Removed unused state update: setTotal((t) => t + 1);
    });
    socket.on("edit_post", (updatedPost) => {
      setPosts((prev) => prev.map((p) => (p._id === updatedPost._id ? updatedPost : p)));
    });
    socket.on("delete_post", (deletedId) => {
      setPosts((prev) => prev.filter((p) => p._id !== deletedId));
      // Removed unused state update: setTotal((t) => (t > 0 ? t - 1 : 0));
    });
    return () => {
      socket.disconnect();
    };
  }, []);

  const lastPostRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new window.IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const loggedIn = !!userInfo && !!userInfo.username;
  return (
    <>
      {!loggedIn && (
        <>
          <section className="text-center pt-24 pb-16">
            <h1 className={`text-5xl md:text-7xl font-extrabold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Ideas that Glow.</h1>
            <p className={`text-lg md:text-xl font-light mb-8 max-w-2xl mx-auto ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              A radiant space to share your stories, insights, and inspirations with a creative community.
            </p>
            <div className="flex justify-center gap-4">
              <a href="/register" className="inline-block bg-yellow-400 text-slate-900 font-semibold px-8 py-3 rounded-full shadow-lg hover:bg-yellow-300 transition">
                Start Writing
              </a>
              <a href="#features" className={`inline-flex items-center gap-2 font-semibold px-8 py-3 rounded-full transition ${theme === 'dark' ? 'text-white/80 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                Learn more <ChevronsRight size={18} />
              </a>
            </div>
          </section>

          <section id="features" className={`py-20 ${theme === 'dark' ? 'bg-black/20' : 'bg-white/50'}`}>
            <div className="max-w-6xl mx-auto px-4">
              <h2 className={`text-4xl font-bold text-center mb-12 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>More Than Just a Blog</h2>
              <div className="grid md:grid-cols-3 gap-10">
                <div className="text-center">
                  <div className={`inline-flex items-center justify-center h-16 w-16 rounded-full mb-4 ${theme === 'dark' ? 'bg-yellow-400/10 text-yellow-400' : 'bg-blue-100 text-blue-600'}`}>
                    <Pen size={28} />
                  </div>
                  <h3 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>Effortless Publishing</h3>
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>A clean, intuitive editor that lets you focus on your writing.</p>
                </div>
                <div className="text-center">
                  <div className={`inline-flex items-center justify-center h-16 w-16 rounded-full mb-4 ${theme === 'dark' ? 'bg-yellow-400/10 text-yellow-400' : 'bg-blue-100 text-blue-600'}`}>
                    <Zap size={28} />
                  </div>
                  <h3 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>AI-Powered Assistance</h3>
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Spark new ideas and refine your drafts with our built-in AI assistant.</p>
                </div>
                <div className="text-center">
                  <div className={`inline-flex items-center justify-center h-16 w-16 rounded-full mb-4 ${theme === 'dark' ? 'bg-yellow-400/10 text-yellow-400' : 'bg-blue-100 text-blue-600'}`}>
                    <Users size={28} />
                  </div>
                  <h3 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>Community Engagement</h3>
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Connect with other creators, share feedback, and grow your audience.</p>
                </div>
              </div>
            </div>
          </section>

          <section className={`py-20 text-center ${theme === 'dark' ? 'bg-black/20' : 'bg-white/50'}`}>
            <h2 className={`text-4xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>Ready to Illuminate Your Ideas?</h2>
            <p className={`max-w-xl mx-auto mb-8 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Join a community of writers, thinkers, and creators. Your next great story starts here.</p>
            <a href="/register" className="inline-block bg-yellow-400 text-slate-900 font-semibold px-10 py-4 rounded-full shadow-lg hover:bg-yellow-300 transition">
              Sign Up For Free
            </a>
          </section>

        </>
      )}

      {/* Trending/Latest Section */}
      <div className="flex items-center gap-2 mb-4">
        <span className={`${theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-blue-100 text-blue-700'} px-3 py-1 rounded-full text-xs font-semibold mr-2`}>Latest</span>
        <h2 className={`${theme === 'dark' ? 'text-white' : 'text-gray-800'} text-2xl font-bold`}>Recent Posts</h2>
      </div>

      {loading && posts.length === 0 ? (
        <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-800'} text-center py-20 text-lg`}>
          Loading posts...
          <svg className="animate-spin h-5 w-5 mx-auto mt-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      ) : (
        <div className="grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {posts.length > 0 && posts.map((post, i) => {
            if (i === posts.length - 1) {
              return <div ref={lastPostRef} key={post._id}><Content {...post} /></div>;
            } else {
              return <Content key={post._id} {...post} />;
            }
          })}
          {loading && Array.from({ length: SKELETON_COUNT }).map((_, i) => <PostSkeleton key={i} theme={theme} />)}
        </div>
      )}

      {!hasMore && posts.length > 0 && (
        <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-800'} text-center py-8 text-sm`}>No more posts to load.</div>
      )}
      {!loading && posts.length === 0 && (
        <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-800'} text-center py-20 text-lg`}>No posts yet. Be the first to share your story!</div>
      )}
    </>
  );
};

export default Post;